import { useAuthStore } from '@/stores/authStore'
import type { InvoiceDownloadResponse, InvoiceHistoryResponse } from '@/types/invoice'
import type { OrgInfoProductApi } from '@/types/organization-owner-api'
import type { ManagePaymentSourcesResponse, RegeneratePaymentLinkResponse, UpgradeResponse } from '@/types/subscription'
import { ownerApiCall } from '../axiosInstance'
import type {
	AddOwnerResponse,
	AttachDeviceRequest,
	AttachDeviceResponse,
	AttachDevicesRequest,
	AttachDevicesResponse,
	CertGenerationOptions,
	CreateOrganizationResponse,
	DeleteOrganizationResponse,
	DeleteOwnerResponse,
	GetOwnersResponse,
	OrganizationBrokerSettings,
	ReprovisionDeviceRequest,
	ReprovisionDeviceResponse,
	ReprovisionDevicesRequest,
	ReprovisionDevicesResponse,
	UpdateOrganizationParams,
	UpdateOrganizationResponse,
} from '../organizationsApi'
import { demoApi } from './db'

export const organizationsApi = {
	createOrganization: async (
		name: string,
		brokerSettings: OrganizationBrokerSettings,
		options?: { planId?: string; period?: 'Monthly' | 'Yearly' },
	): Promise<CreateOrganizationResponse> => {
		const id = crypto.randomUUID()
		const userEmail = useAuthStore.getState().user?.email || 'demo_user'
		const body: {
			id: string
			name: string
			userEmail: string
			broker_host: string
			broker_port: number
			planId?: string
			period?: 'Monthly' | 'Yearly'
		} = {
			id,
			name,
			userEmail,
			...brokerSettings,
		}
		if (options?.planId && options?.period) {
			body.planId = options.planId
			body.period = options.period
		}
		try {
			await demoApi.postNoTenant('/organizations', body)
		} catch (error) {
			console.error('Failed to create organization:', error)
			throw error
		}
		return { id, status: 'ok' }
	},
	regeneratePaymentLink: async (organizationId: string): Promise<RegeneratePaymentLinkResponse> => {
		return ownerApiCall<RegeneratePaymentLinkResponse>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/regenerate-payment-link`,
			{
				method: 'POST',
			},
		)
	},
	subscribeOrganization: async (
		organizationId: string,
		planId: string,
		period: 'Yearly' | 'Monthly',
	): Promise<UpgradeResponse> => {
		try {
			await demoApi.post(`/organizations/${organizationId}/subscribe`, { planId, period })
			return { status: 'upgraded' }
		} catch (error) {
			console.error('Failed to persist subscription:', error)
			throw error
		}
	},
	modifySubscription: async (
		organizationId: string,
		planId: string,
		period: 'Yearly' | 'Monthly',
	): Promise<UpgradeResponse> => {
		try {
			await demoApi.put(`/organizations/${organizationId}/subscription`, { planId, period })
			return { status: 'upgraded' }
		} catch (error) {
			console.error('Failed to update subscription:', error)
			throw error
		}
	},
	getOwners: async (organizationId: string): Promise<GetOwnersResponse> => {
		const res = await demoApi.getNoTenant<{ results: string[] }>(`/organizations/${organizationId}/owners`)
		return { results: res.results ?? [] }
	},
	addOwner: async (organizationId: string, email: string): Promise<AddOwnerResponse> => {
		await demoApi.postNoTenant(`/v3/organizations/${organizationId}/owners`, { email })
		return { status: 'ok' }
	},
	deleteOwner: async (organizationId: string, email: string): Promise<DeleteOwnerResponse> => {
		await demoApi.deleteNoTenant(`/v3/organizations/${organizationId}/owners`, { email })
		return { status: 'ok' }
	},
	deleteOrganization: async (organizationId: string): Promise<DeleteOrganizationResponse> => {
		await demoApi.deleteNoTenant(`/organizations/${organizationId}`)

		return { status: 'ok' }
	},
	updateOrganization: async (
		organizationId: string,
		params: UpdateOrganizationParams,
	): Promise<UpdateOrganizationResponse> => {
		return demoApi.putNoTenant<UpdateOrganizationResponse>(`/organizations/${organizationId}`, params)
	},
	getInvoices: async (organizationId: string): Promise<InvoiceHistoryResponse> => {
		return ownerApiCall<InvoiceHistoryResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/invoices`)
	},
	downloadInvoice: async (organizationId: string, invoiceId: string): Promise<InvoiceDownloadResponse> => {
		return ownerApiCall<InvoiceDownloadResponse>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/invoices/${encodeURIComponent(invoiceId)}/download`,
		)
	},
	managePaymentSources: async (organizationId: string): Promise<ManagePaymentSourcesResponse> => {
		return ownerApiCall<ManagePaymentSourcesResponse>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/manage_payment_sources`,
			{
				method: 'POST',
			},
		)
	},
	getSubscriptionInfo: async (organizationId: string): Promise<OrgInfoProductApi> => {
		return demoApi.getNoTenant<OrgInfoProductApi>(`/organizations/${organizationId}/subscription`)
	},
	createTenant: async (
		organizationId: string,
		body: { name: string; template?: 'Empty' | 'ByteOne' | 'Uplink'; frontend_settings?: Record<string, unknown> },
	) => {
		return demoApi.postNoTenant<{ status: 'ok'; id: string }>(`/organizations/${organizationId}/tenants`, body)
	},
	activateTenant: async (organizationId: string, tenantId: string) => {
		return demoApi.postNoTenant<{ status: 'ok' }>(
			`/organizations/${organizationId}/tenants/${encodeURIComponent(tenantId)}/activate`,
		)
	},
	deactivateTenant: async (organizationId: string, tenantId: string) => {
		return demoApi.postNoTenant<{ status: 'ok' }>(
			`/organizations/${organizationId}/tenants/${encodeURIComponent(tenantId)}/deactivate`,
		)
	},
	deleteTenant: async (organizationId: string, tenantId: string) => {
		return demoApi.postNoTenant<{ status: 'ok' }>(
			`/organizations/${organizationId}/tenants/${encodeURIComponent(tenantId)}/delete`,
		)
	},
	reprovisionDevice: async (
		organizationId: string,
		uid: number,
		params: ReprovisionDeviceRequest,
	): Promise<ReprovisionDeviceResponse> => {
		return demoApi.postNoTenant<ReprovisionDeviceResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/reprovision`,
			params,
		)
	},
	detachDevice: async (
		organizationId: string,
		uid: number,
	): Promise<{ uid: number; tenant_id: null; device_id: null }> => {
		return demoApi.postNoTenant<{ uid: number; tenant_id: null; device_id: null }>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/detach`,
		)
	},
	// Detach is expressed through /reprovision: the device is reprovisioned into the root
	// project keyed by its UID. Cert generation is off unless the user opts in.
	detachDeviceViaReprovision: async (
		organizationId: string,
		uid: number,
		rootTenantId: string,
		options?: CertGenerationOptions,
	): Promise<ReprovisionDeviceResponse> => {
		return organizationsApi.reprovisionDevice(organizationId, uid, {
			target_tenant_id: rootTenantId,
			target_device_id: String(uid),
			generate_mtls_certs: options?.generate_mtls_certs ?? false,
			generate_http_certs: options?.generate_http_certs ?? false,
		})
	},
	reprovisionDevices: async (
		organizationId: string,
		body: ReprovisionDevicesRequest,
	): Promise<ReprovisionDevicesResponse> => {
		return demoApi.postNoTenant<ReprovisionDevicesResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/reprovision`,
			body,
		)
	},
	// Bulk detach is expressed through the bulk /reprovision endpoint: each device is
	// reprovisioned into the root project keyed by its own UID, in one request.
	detachDevicesViaReprovision: async (
		organizationId: string,
		uids: number[],
		rootTenantId: string,
		options?: CertGenerationOptions,
	): Promise<ReprovisionDevicesResponse> => {
		return organizationsApi.reprovisionDevices(organizationId, {
			devices: uids.map(uid => ({
				uid,
				target_tenant_id: rootTenantId,
				target_device_id: String(uid),
			})),
			generate_mtls_certs: options?.generate_mtls_certs ?? false,
			generate_http_certs: options?.generate_http_certs ?? false,
		})
	},
	// Attach is expressed through /reprovision: the root-project device (uid) is
	// reprovisioned into the target project under the given device id.
	attachDeviceViaReprovision: async (
		organizationId: string,
		uid: number,
		targetTenantId: string,
		targetDeviceId: string,
		options?: CertGenerationOptions,
	): Promise<ReprovisionDeviceResponse> => {
		return organizationsApi.reprovisionDevice(organizationId, uid, {
			target_tenant_id: targetTenantId,
			target_device_id: targetDeviceId,
			generate_mtls_certs: options?.generate_mtls_certs ?? false,
			generate_http_certs: options?.generate_http_certs ?? false,
		})
	},
	attachDevice: async (
		organizationId: string,
		uid: number,
		params: AttachDeviceRequest,
	): Promise<AttachDeviceResponse> => {
		return demoApi.postNoTenant<AttachDeviceResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/attach`,
			params,
		)
	},
	getNextUid: async (organizationId: string): Promise<{ nextUid: number }> => {
		return demoApi.getNoTenant<{ nextUid: number }>(`/organizations/${organizationId}/next-uid`)
	},
	attachDevices: async (organizationId: string, body: AttachDevicesRequest): Promise<AttachDevicesResponse> => {
		return demoApi.postNoTenant<AttachDevicesResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/attach`,
			body,
		)
	},
}
